import { useState, useCallback, useEffect } from "react";

const STORAGE_KEY = "reoccurring-reviewed";

/**
 * Load reviewed items from localStorage
 */
function loadFromStorage() {
	try {
		const saved = localStorage.getItem(STORAGE_KEY);
		if (saved) {
			return JSON.parse(saved);
		}
	} catch (e) {
		console.warn("Error loading reviewed items from localStorage:", e);
	}
	return { groups: {}, transactions: {} };
}

/**
 * Save reviewed items to localStorage
 */
function saveToStorage(data) {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
	} catch (e) {
		console.warn("Error saving reviewed items to localStorage:", e);
	}
}

/**
 * Hook to manage reviewed state for recurring transaction groups and individual transactions
 */
export default function useReviewedItems() {
	const [reviewedData, setReviewedData] = useState(() => loadFromStorage());

	// Persist changes to localStorage
	useEffect(() => {
		saveToStorage(reviewedData);
	}, [reviewedData]);

	/**
	 * Check if a group is marked as reviewed
	 */
	const isGroupReviewed = useCallback(
		(groupId) => {
			return reviewedData.groups[groupId]?.reviewed === true;
		},
		[reviewedData]
	);

	/**
	 * Check if a transaction is marked as reviewed
	 */
	const isTransactionReviewed = useCallback(
		(transactionId) => {
			return reviewedData.transactions[transactionId]?.reviewed === true;
		},
		[reviewedData]
	);

	/**
	 * Mark a group as reviewed or unreviewed
	 */
	const markGroupReviewed = useCallback((groupId, reviewed = true) => {
		setReviewedData((prev) => ({
			...prev,
			groups: {
				...prev.groups,
				[groupId]: reviewed
					? { reviewed: true, reviewedAt: new Date().toISOString() }
					: undefined,
			},
		}));
	}, []);

	/**
	 * Mark a transaction as reviewed or unreviewed
	 */
	const markTransactionReviewed = useCallback((transactionId, reviewed = true) => {
		setReviewedData((prev) => ({
			...prev,
			transactions: {
				...prev.transactions,
				[transactionId]: reviewed
					? { reviewed: true, reviewedAt: new Date().toISOString() }
					: undefined,
			},
		}));
	}, []);

	/**
	 * Toggle group reviewed state
	 */
	const toggleGroupReviewed = useCallback(
		(groupId) => {
			const isCurrentlyReviewed = isGroupReviewed(groupId);
			markGroupReviewed(groupId, !isCurrentlyReviewed);
		},
		[isGroupReviewed, markGroupReviewed]
	);

	/**
	 * Toggle transaction reviewed state
	 */
	const toggleTransactionReviewed = useCallback(
		(transactionId) => {
			const isCurrentlyReviewed = isTransactionReviewed(transactionId);
			markTransactionReviewed(transactionId, !isCurrentlyReviewed);
		},
		[isTransactionReviewed, markTransactionReviewed]
	);

	/**
	 * Clear all reviewed data
	 */
	const clearAllReviewed = useCallback(() => {
		setReviewedData({ groups: {}, transactions: {} });
	}, []);

	/**
	 * Get count of reviewed groups
	 */
	const reviewedGroupCount = Object.values(reviewedData.groups).filter(
		(g) => g?.reviewed
	).length;

	/**
	 * Get count of reviewed transactions
	 */
	const reviewedTransactionCount = Object.values(reviewedData.transactions).filter(
		(t) => t?.reviewed
	).length;

	return {
		isGroupReviewed,
		isTransactionReviewed,
		markGroupReviewed,
		markTransactionReviewed,
		toggleGroupReviewed,
		toggleTransactionReviewed,
		clearAllReviewed,
		reviewedGroupCount,
		reviewedTransactionCount,
	};
}
